import CheckIcon from '@mui/icons-material/Check';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureEvent, identifyUser } from '~/analytics/posthog';
import {
  JourneyEquipmentAutopropelidoIcon,
  JourneyEquipmentBikeIcon,
  JourneyEquipmentCiclomotorIcon,
  JourneyEquipmentEBikeIcon
} from '~/assets/icons/JourneyEquipmentTypeIcons';
import JourneySupportDrawer from '~/components/StandardJourney/JourneySupportDrawer';
import { useJourneyAttribution } from '~/components/StandardJourney/useJourneyAttribution';
import {
  StandardJourneyBrandedScreen,
  StandardJourneyCardHeader,
  JourneyLoadingScreen,
  StandardJourneyFieldError,
  StandardJourneyHintIcon,
  StandardJourneyInput,
  StandardJourneySecondaryAction
} from '~/components/StandardJourney';
import { getUserDetails, saveMobilePhone, sendCode, validateCode } from '~/services/modules/users';
import { isValidBrazilianMobile, userHasNoPhone } from '~/utils/phoneValidation';
import { classifyPhoneSaveError, nextStepAfterCode, nextStepAfterPhone, validateCodeConflict } from './afterCodeStep';
import { genderFromValidateCode } from './genderFromValidateCode';
import type { UserDetailsResponse } from '~/services/modules/users/types';
import useStore, { storeState } from '~/store';
import type { EquipmentValue } from '~/utils/searchNameEquipment';
import { maskCurrencyInput, parseCurrencyToNumber } from '~/utils/currencyMask';
import { parseParamToString } from '~/utils/parseParamToString';
import { reportError } from '~/utils/reportError';
import { withoutJourneyWizard } from '../journeyQuery';
import { buildPersonalCoverageQuoteUrl } from './buildPersonalCoverageQuoteUrl';
import { maskCpfForDisplay } from './cpfMask';
import { emailsMatch, isValidEmail } from './emailValidation';
import { isCompleteBrazilianPhone, onlyDigitsPhone } from './phoneValidation';
import { buildSimplifiedQuotePayload } from './quotePayload';
import { birthDateToIso, isValidBirthDate } from './birthDateValidation';

const QUOTE_CREATION_TIMEOUT_MS = 15_000;

/** Copy dos estados de erro do Figma (secção "Previsão de erro"). */
const ERROR_MESSAGES = {
  cpf: 'Esse CPF não parece válido. Confira os números.',
  verificationCode: 'Código inválido',
  email: 'Por favor, insira um endereço de e-mail válido.',
  confirmEmail: 'O e-mail de confirmação deve ser igual ao e-mail acima.',
  equipmentValue: 'Insira o valor do veículo.',
  yearOfManufacture: 'Indique o ano de fabricação.'
} as const;

export type QuotationWizardStep =
  | 'cpf'
  | 'cpf_loading'
  | 'not_recognized'
  | 'profile_found'
  | 'verify_channel'
  | 'verify_code'
  | 'phone_after_code'
  | 'gender_after_code'
  | 'manual_person'
  | 'equipment'
  | 'quote_loading';

const STEP_PROGRESS: Record<QuotationWizardStep, number> = {
  cpf: 18,
  cpf_loading: 20,
  not_recognized: 18,
  profile_found: 28,
  verify_channel: 33,
  verify_code: 38,
  phone_after_code: 39,
  gender_after_code: 40,
  manual_person: 42,
  equipment: 64,
  quote_loading: 100
};

const EQUIPMENT_OPTIONS: { id: EquipmentValue; label: string; description: string }[] = [
  {
    id: 'bike',
    label: 'Bicicleta',
    description:
      'Veículo de duas rodas movido exclusivamente pela força humana, não sendo considerado, para fins do CTB, equivalente à motocicleta, motoneta ou ciclomotor.'
  },
  {
    id: 'ebike',
    label: 'Bicicleta elétrica',
    description:
      'Veículo de duas rodas movido principalmente pela força humana, equipado com motor auxiliar de até 1.000 W, que funciona somente enquanto o condutor pedala (pedal assistido). Não pode possuir acelerador ou qualquer controle manual de potência, e a assistência do motor deve se limitar à velocidade máxima de 32 km/h.'
  },
  {
    id: 'escooter',
    label: 'Autopropelido',
    description:
      'Equipamento de mobilidade com uma ou mais rodas, podendo ou não possuir sistema de auto equilíbrio, equipado com motor de até 1.000 W, velocidade máxima de fabricação de 32 km/h, largura máxima de 70 cm e distância entre eixos de até 130 cm.'
  },
  {
    id: 'scooter',
    label: 'Ciclomotor e Moto elétrica',
    description:
      'Veículo de 2 ou 3 rodas, equipado com motor elétrico de até 4.000W, cuja velocidade máxima de fabricação não ultrapasse 50 km/h.'
  }
];

const EQUIPMENT_OPTION_ICON = {
  bike: JourneyEquipmentBikeIcon,
  ebike: JourneyEquipmentEBikeIcon,
  escooter: JourneyEquipmentAutopropelidoIcon,
  scooter: JourneyEquipmentCiclomotorIcon
} as const;

function maskEmail(email: string) {
  if (!email.includes('@')) return 'ta*********@gmail.com';
  const [user, domain] = email.split('@');
  const safeUser = `${user.slice(0, 2)}${'*'.repeat(Math.max(1, user.length - 2))}`;
  return `${safeUser}@${domain}`;
}

function formatBrazilianCpf(doc: string) {
  const digits = doc.replace(/\D/g, '');
  if (digits.length !== 11) return doc;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

function mapGenderToStore(value: string): 'm' | 'f' {
  return value === 'masculino' ? 'm' : 'f';
}

function onlyDigitsCpf(raw: string) {
  return raw.replace(/\D/g, '').slice(0, 11);
}

export type QuotationJourneyProps = {
  initialStack?: QuotationWizardStep[];
  /** Apenas Storybook / testes: utilizador “encontrado” ao abrir em `profile_found`, `verify_channel` ou `verify_code`. */
  storyRegisteredUser?: UserDetailsResponse | null;
  /** Apenas Storybook / testes: canal já escolhido ao abrir direto em `verify_code` (1 = SMS, 2 = e-mail). */
  storyVerifyChannel?: 1 | 2;
};

export default function StandardJourneyFlow({
  initialStack,
  storyRegisteredUser = null,
  storyVerifyChannel
}: QuotationJourneyProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stack, setStack] = useState<QuotationWizardStep[]>(() => initialStack ?? ['cpf']);
  const step = stack[stack.length - 1];

  useEffect(() => {
    captureEvent('journey_step_viewed', { step });
  }, [step]);

  const push = useCallback((s: QuotationWizardStep) => {
    setStack((prev) => [...prev, s]);
  }, []);

  const pop = useCallback(() => {
    setStack((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  }, []);

  const replaceTop = useCallback((s: QuotationWizardStep) => {
    setStack((prev) => [...prev.slice(0, -1), s]);
  }, []);

  const [cpfInput, setCpfInput] = useState('');
  const [registeredUser, setRegisteredUser] = useState<UserDetailsResponse | null>(() => storyRegisteredUser);

  const [name, setName] = useState('');
  const [socialNameEnabled, setSocialNameEnabled] = useState(false);
  const [socialName, setSocialName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('');
  const [manualCpf, setManualCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');

  const [cpfError, setCpfError] = useState(false);
  const [verificationCodeError, setVerificationCodeError] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [confirmEmailError, setConfirmEmailError] = useState(false);
  const [equipmentValueError, setEquipmentValueError] = useState(false);
  const [yearError, setYearError] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [verifyChannel, setVerifyChannel] = useState<'email' | 'sms'>('email');
  const [usedCodeType, setUsedCodeType] = useState<1 | 2 | null>(() => storyVerifyChannel ?? null);
  // CORE-1025: token do validate-code (autoriza gravar o celular) e gênero devolvido, usados no passo do celular.
  const [sessionToken, setSessionToken] = useState('');
  const [validatedGender, setValidatedGender] = useState<string | undefined>();
  const [phoneAfterCode, setPhoneAfterCode] = useState('');
  const [phoneAfterCodeError, setPhoneAfterCodeError] = useState<string | null>(null);
  const [phoneSaving, setPhoneSaving] = useState(false);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  const [equipmentKey, setEquipmentKey] = useState<EquipmentValue>('bike');
  const [equipmentValueInput, setEquipmentValueInput] = useState('');
  const [isNewEquipment, setIsNewEquipment] = useState(true);
  const [hasInvoice, setHasInvoice] = useState(false);
  const [yearOfManufacture, setYearOfManufacture] = useState<number | ''>('');

  const {
    quoteAsyncPost,
    SetName,
    SetPhoneNumber,
    SetEmail,
    SetCpf,
    setGender: setStoreGender,
    setIsSocialName,
    SetCustomerId,
    SetBirthDate,
    setEquipment,
    setEquipmentValue,
    setYearOfManufacture: setStoreYear,
    setHasInvoice: setStoreHasInvoice,
    setIsNewEquipment: setStoreIsNew,
    setAccessoriesValue
  } = useStore((store) => ({
    quoteAsyncPost: store.quoteAsyncPost,
    SetName: store.SetName,
    SetPhoneNumber: store.SetPhoneNumber,
    SetEmail: store.SetEmail,
    SetCpf: store.SetCpf,
    setGender: store.setGender,
    setIsSocialName: store.setIsSocialName,
    SetCustomerId: store.SetCustomerId,
    SetBirthDate: store.SetBirthDate,
    setEquipment: store.setEquipment,
    setEquipmentValue: store.setEquipmentValue,
    setYearOfManufacture: store.setYearOfManufacture,
    setHasInvoice: store.setHasInvoice,
    setIsNewEquipment: store.setIsNewEquipment,
    setAccessoriesValue: store.setAccessoriesValue
  }));

  const { clubName, sellerName } = useJourneyAttribution();
  const cardHeader = (
    <StandardJourneyCardHeader appId="journey-card-header" clubName={clubName} sellerName={sellerName} />
  );

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 26 }, (_, i) => y - i);
  }, []);

  const syncQuoteFromPersonForm = useCallback(() => {
    SetName(socialNameEnabled && socialName ? socialName : name);
    SetPhoneNumber(onlyDigitsPhone(phone));
    SetEmail(email);
    SetCpf(manualCpf);
    setStoreGender(mapGenderToStore(gender));
    setIsSocialName(socialNameEnabled);
    SetBirthDate(birthDateToIso(birthDate));
  }, [
    SetName,
    SetPhoneNumber,
    SetEmail,
    SetCpf,
    setStoreGender,
    setIsSocialName,
    SetBirthDate,
    email,
    gender,
    name,
    phone,
    socialName,
    socialNameEnabled,
    manualCpf,
    birthDate
  ]);

  const syncRegisteredToForm = useCallback(
    (u: UserDetailsResponse) => {
      SetName(u.name);
      SetCpf(u.document_number);
      if (u.masked_email) {
        SetEmail('');
      }
    },
    [SetName, SetCpf, SetEmail]
  );

  const handleHeaderBack = useCallback(() => {
    if (stack.length <= 1) {
      navigate({ pathname: '/', search: withoutJourneyWizard(searchParams).toString() });
      return;
    }
    pop();
  }, [navigate, pop, searchParams, stack.length]);

  const handleCpfAdvance = async () => {
    const doc = onlyDigitsCpf(cpfInput);
    if (doc.length !== 11) {
      setCpfError(true);
      return;
    }
    setCpfError(false);
    push('cpf_loading');
    SetCpf(doc);
    setDetailsLoading(true);
    try {
      const data = await getUserDetails(doc);
      const found = Boolean(data?.document_number || data?.name);
      setRegisteredUser(found ? data : null);
      if (found && data) {
        replaceTop('profile_found');
      } else {
        setManualCpf(doc);
        setStack(['cpf', 'manual_person']);
      }
    } catch {
      setManualCpf(doc);
      setStack(['cpf', 'manual_person']);
      storeState.addToast({
        title: 'Aviso',
        message: 'Não foi possível consultar o CPF agora. Preencha seus dados manualmente.',
        type: 'warning'
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const navigateToCoverageWithQuote = useCallback(() => {
    const { quote: q, quoteResponse: qr } = useStore.getState();
    navigate(
      buildPersonalCoverageQuoteUrl({
        organizationId: q.organization_id,
        planIdFromStore: q.plan_id,
        quoteHash: qr?.quote_hash,
        searchParams,
        seller: q.seller
      })
    );
  }, [navigate, searchParams]);

  const submitQuote = async () => {
    const parsedValue = parseCurrencyToNumber(equipmentValueInput);
    const invalidValue = !Number.isFinite(parsedValue) || parsedValue <= 0;
    const invalidYear = yearOfManufacture === '';
    setEquipmentValueError(invalidValue);
    setYearError(invalidYear);
    if (invalidValue || invalidYear) return;

    setEquipment(equipmentKey);
    setEquipmentValue(parsedValue);
    setStoreYear(Number(yearOfManufacture));
    setStoreHasInvoice(hasInvoice);
    setStoreIsNew(isNewEquipment);
    setAccessoriesValue(0);

    push('quote_loading');
    setQuoteSubmitting(true);
    const timeoutError = new Error('QUOTE_CREATION_TIMEOUT');
    try {
      const quoteSnapshot = useStore.getState().quote;
      const body = buildSimplifiedQuotePayload(quoteSnapshot, searchParams.get('plan'));
      await Promise.race([
        quoteAsyncPost(body),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(timeoutError), QUOTE_CREATION_TIMEOUT_MS);
        })
      ]);
      navigateToCoverageWithQuote();
    } catch (error: unknown) {
      pop();
      if (error instanceof Error && error.message === 'QUOTE_CREATION_TIMEOUT') {
        storeState.addToast({
          title: 'Demora na cotação',
          message: 'A operação ultrapassou 15 segundos. Verifique a sua ligação e tente novamente.',
          type: 'warning'
        });
      } else {
        reportError('Erro ao criar cotação (jornada 2026)', error as Error, {
          context: 'StandardJourneyFlow_submitQuote',
          planParam: parseParamToString(searchParams.get('plan') ?? undefined)
        });
        let message = 'Não foi possível processar a sua cotação no momento. Por favor, tente novamente mais tarde.';
        if (error && typeof error === 'object' && 'isAxiosError' in error) {
          const notifications = (error as { response?: { data?: { notifications?: Array<{ message?: string }> } } })
            .response?.data?.notifications;
          if (notifications?.[0]?.message) message = notifications[0].message;
        }
        storeState.addToast({ title: 'Erro', message, type: 'error' });
      }
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleGoToVerifyChannel = () => {
    if (!registeredUser) return;
    setVerifyChannel('email');
    push('verify_channel');
  };

  const handleSendVerificationCode = async () => {
    if (!registeredUser?.document_number) return;
    const codeType: 1 | 2 = verifyChannel === 'sms' ? 1 : 2;
    setSendCodeLoading(true);
    try {
      await sendCode(registeredUser.document_number, codeType);
      setUsedCodeType(codeType);
      syncRegisteredToForm(registeredUser);
      push('verify_code');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { notifications?: Array<{ message?: string }> } } })?.response?.data
          ?.notifications?.[0]?.message ?? 'Não foi possível enviar o código de verificação. Tente novamente.';
      storeState.addToast({ title: 'Erro', message, type: 'error' });
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleValidateCode = async () => {
    const doc = registeredUser?.document_number ?? onlyDigitsCpf(cpfInput);
    if (!doc || usedCodeType == null || !verificationCode.trim()) return;
    setValidateLoading(true);
    try {
      // Com a organização, o api-core devolve 409 quando o CPF é de outra organização (em vez de seguir e
      // a cotação ir com o person_id errado).
      const organizationId = searchParams.get('org') || useStore.getState().quote.organization_id || undefined;
      const result = await validateCode(doc, verificationCode.trim(), usedCodeType, organizationId);
      setSessionToken(result.token ?? '');
      setValidatedGender(result.gender);
      if (result.person_id) {
        SetCustomerId(result.person_id);
        identifyUser(String(result.person_id));
      }
      if (result.name) SetName(result.name);
      if (result.username) SetEmail(result.username);
      SetCpf(doc);
      setAccessoriesValue(0);
      setVerificationCodeError(null);
      // Cadastro sem celular pede o celular (CORE-1025); depois o gênero quando falta; senão o equipamento.
      const registeredGender = genderFromValidateCode(result.gender);
      if (registeredGender) setStoreGender(registeredGender);
      setPhoneAfterCode('');
      setPhoneAfterCodeError(null);
      push(nextStepAfterCode(registeredUser, result.gender));
    } catch (error: unknown) {
      if (validateCodeConflict(error)) SetCustomerId('');
      const message =
        (error as { response?: { data?: { notifications?: Array<{ message?: string }> } } })?.response?.data
          ?.notifications?.[0]?.message ?? ERROR_MESSAGES.verificationCode;
      setVerificationCodeError(message);
    } finally {
      setValidateLoading(false);
    }
  };

  const handleSavePhoneAfterCode = async () => {
    if (!isValidBrazilianMobile(phoneAfterCode)) {
      setPhoneAfterCodeError('Informe um celular válido com DDD.');
      return;
    }
    const digits = onlyDigitsPhone(phoneAfterCode);
    setPhoneSaving(true);
    try {
      await saveMobilePhone(digits, sessionToken);
      SetPhoneNumber(digits);
      push(nextStepAfterPhone(validatedGender));
    } catch (error: unknown) {
      const outcome = classifyPhoneSaveError(error);
      if (outcome.kind === 'continue') {
        SetPhoneNumber(digits);
        push(nextStepAfterPhone(validatedGender));
        return;
      }
      reportError('Erro ao salvar celular após validar o código', error as Error, {
        context: 'journey_phone_after_code'
      });
      if (outcome.kind === 'session_expired') {
        // O token do código expirou: o customer_id deixa de valer e o código tem de ser pedido de novo.
        SetCustomerId('');
        setVerificationCode('');
        storeState.addToast({ title: 'Erro', message: outcome.message, type: 'error' });
        setStack(['cpf', 'profile_found', 'verify_channel']);
        return;
      }
      setPhoneAfterCodeError(outcome.message);
    } finally {
      setPhoneSaving(false);
    }
  };

  const canContinueManual = useMemo(() => {
    const doc = onlyDigitsCpf(manualCpf);
    if (!name || !isCompleteBrazilianPhone(phone) || !email || !gender || doc.length !== 11) return false;
    if (!isValidBirthDate(birthDate)) return false;
    if (socialNameEnabled && !socialName) return false;
    return true;
  }, [birthDate, email, gender, manualCpf, name, phone, socialName, socialNameEnabled]);

  const handleManualContinue = () => {
    const invalidEmail = !isValidEmail(email);
    const mismatchedEmail = !invalidEmail && !emailsMatch(email, confirmEmail);
    setEmailError(invalidEmail);
    setConfirmEmailError(mismatchedEmail);
    if (invalidEmail || mismatchedEmail || !canContinueManual) return;
    syncQuoteFromPersonForm();
    setAccessoriesValue(0);
    push('equipment');
  };

  const displayEmailMasked = registeredUser?.masked_email ?? maskEmail(email);
  const displayName = registeredUser?.name ?? name;
  const displayCpf = maskCpfForDisplay(registeredUser ? registeredUser.document_number : manualCpf);

  const progress = STEP_PROGRESS[step];

  const screen = (
    <StandardJourneyBrandedScreen
      appId="quotation-standard-journey"
      // Cartão no meio da tela (vertical e horizontal), não colado no topo — ex.: «Vamos começar pela sua identificação».
      centerCard
      contentLayout={step === 'quote_loading' ? 'fullscreen' : 'card'}
      onBack={handleHeaderBack}
      progressPercent={progress}
      // Sem a tela de boas-vindas, o primeiro passo é a entrada do fluxo: não há para onde voltar.
      showBack={stack.length > 1}
    >
      {step === 'cpf' ? (
        <>
          {cardHeader}
          <Typography align="center" sx={{ mb: 2 }} variant="subtitle1">
            Vamos começar pela sua identificação
          </Typography>
          <StandardJourneyInput
            appId="journey-cpf"
            error={cpfError}
            errorMessage={ERROR_MESSAGES.cpf}
            label="CPF"
            onChange={(v) => {
              setCpfInput(onlyDigitsCpf(v));
              setCpfError(false);
            }}
            placeholder="000.000.000-00"
            value={formatBrazilianCpf(cpfInput)}
          />
          <Typography
            data-app-id="journey-cpf-hint"
            sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
            variant="caption"
          >
            Com o CPF a gente verifica se você já tem cadastro e agiliza sua contratação.
          </Typography>
          <LoadingButton
            data-app-id="journey-cpf-advance"
            fullWidth
            loading={detailsLoading}
            onClick={() => {
              void handleCpfAdvance();
            }}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Continuar
          </LoadingButton>
        </>
      ) : null}

      {step === 'cpf_loading' ? (
        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 2, py: 3 }}>
          <Typography align="center" variant="subtitle1">
            Buscando informações cadastrais…
          </Typography>
          <LoadingButton fullWidth loading size="large" variant="contained">
            Aguarde
          </LoadingButton>
        </Box>
      ) : null}

      {step === 'not_recognized' ? (
        <>
          <Typography align="center" variant="subtitle1">
            Não reconhece este cadastro?
          </Typography>
          <Typography align="center" sx={{ color: 'text.secondary', mb: 2 }} variant="body2">
            Para sua segurança, entre em contato com o nosso time de suporte para revisar os dados de cadastro.
          </Typography>
          <Button
            data-app-id="journey-not-recognized-support"
            fullWidth
            onClick={() => setSupportOpen(true)}
            size="large"
            variant="contained"
          >
            Falar com suporte
          </Button>
        </>
      ) : null}

      {step === 'profile_found' && registeredUser ? (
        <>
          <Box
            aria-hidden
            data-app-id="journey-profile-success-icon"
            sx={{
              alignItems: 'center',
              bgcolor: 'success.main',
              borderRadius: '100px',
              color: 'success.contrastText',
              display: 'flex',
              height: 64,
              justifyContent: 'center',
              mx: 'auto',
              width: 64
            }}
          >
            <CheckIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography align="center" sx={{ mt: 2 }} variant="subtitle1">
            Encontramos seu cadastro!
          </Typography>
          <Typography align="center" sx={{ mb: 2 }} variant="body2">
            Confirme seus dados para continuar a cotação.
          </Typography>
          <Box data-app-id="journey-profile-data" sx={{ display: 'grid', gap: 1 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">Nome:</Typography>
              <Typography variant="body1">{displayName}</Typography>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">E-mail:</Typography>
              <Typography variant="body1">{displayEmailMasked}</Typography>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">CPF:</Typography>
              <Typography variant="body1">{displayCpf}</Typography>
            </Box>
          </Box>
          <Button
            data-app-id="journey-profile-use-registration"
            fullWidth
            onClick={handleGoToVerifyChannel}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Confirmar e continuar
          </Button>
          <StandardJourneySecondaryAction
            appId="journey-profile-not-me"
            label="Não reconheço este cadastro"
            onClick={() => {
              setCpfInput('');
              replaceTop('not_recognized');
            }}
          />
        </>
      ) : null}

      {step === 'verify_channel' && registeredUser ? (
        <>
          <Typography align="center" sx={{ mb: 2 }} variant="subtitle1">
            Como você deseja receber seu código de verificação?
          </Typography>
          <RadioGroup
            data-app-id="journey-verify-channel-radio"
            onChange={(event) => setVerifyChannel(event.target.value as 'email' | 'sms')}
            value={verifyChannel}
          >
            <FormControlLabel
              control={<Radio />}
              data-app-id="journey-verify-channel-email"
              label={
                <Typography variant="body1">
                  <strong>E-mail:</strong> {displayEmailMasked}
                </Typography>
              }
              sx={{ alignItems: 'center', m: 0 }}
              value="email"
            />
            <FormControlLabel
              control={<Radio />}
              data-app-id="journey-verify-channel-sms"
              disabled={userHasNoPhone(registeredUser) || !registeredUser.masked_phone?.trim()}
              label={
                <Typography variant="body1">
                  <strong>SMS:</strong> {registeredUser.masked_phone?.trim() || 'Telefone não cadastrado'}
                </Typography>
              }
              sx={{ alignItems: 'center', m: 0 }}
              value="sms"
            />
          </RadioGroup>
          {userHasNoPhone(registeredUser) ? (
            <Typography
              data-app-id="journey-verify-channel-no-phone"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              variant="caption"
            >
              Seu cadastro não tem celular cadastrado, por isso o código será enviado por e-mail.
            </Typography>
          ) : null}
          <LoadingButton
            data-app-id="journey-verify-channel-submit"
            disabled={sendCodeLoading}
            fullWidth
            loading={sendCodeLoading}
            onClick={() => {
              void handleSendVerificationCode();
            }}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Enviar código de verificação
          </LoadingButton>
        </>
      ) : null}

      {step === 'verify_code' ? (
        <>
          <Typography align="center" sx={{ mb: 2 }} variant="subtitle1">
            {usedCodeType === 1
              ? `Enviamos um código de verificação para o número ${registeredUser?.masked_phone ?? ''}.`
              : `Enviamos um código de verificação para o e-mail ${displayEmailMasked}.`}
          </Typography>
          <StandardJourneyInput
            appId="journey-verify-code"
            error={Boolean(verificationCodeError)}
            errorMessage={verificationCodeError ?? undefined}
            label="Código de verificação"
            onChange={(v) => {
              setVerificationCode(v);
              setVerificationCodeError(null);
            }}
            value={verificationCode}
          />
          <LoadingButton
            data-app-id="journey-verify-submit"
            disabled={!verificationCode.trim() || validateLoading}
            fullWidth
            loading={validateLoading}
            onClick={() => {
              void handleValidateCode();
            }}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Validar e continuar
          </LoadingButton>
        </>
      ) : null}

      {step === 'phone_after_code' ? (
        <>
          <Typography align="center" data-app-id="journey-phone-after-code-title" sx={{ mb: 2 }} variant="subtitle1">
            Não encontramos um celular no seu cadastro. Qual é o seu celular com DDD?
          </Typography>
          <StandardJourneyInput
            appId="journey-phone-after-code"
            error={Boolean(phoneAfterCodeError)}
            errorMessage={phoneAfterCodeError ?? undefined}
            label="Celular"
            mask="phone"
            onChange={(v) => {
              setPhoneAfterCode(v);
              setPhoneAfterCodeError(null);
            }}
            value={phoneAfterCode}
          />
          <LoadingButton
            data-app-id="journey-phone-after-code-continue"
            disabled={!isValidBrazilianMobile(phoneAfterCode) || phoneSaving}
            fullWidth
            loading={phoneSaving}
            onClick={() => {
              void handleSavePhoneAfterCode();
            }}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Continuar
          </LoadingButton>
        </>
      ) : null}

      {step === 'gender_after_code' ? (
        <>
          <Typography align="center" data-app-id="journey-gender-after-code-title" sx={{ mb: 2 }} variant="subtitle1">
            Antes de continuar selecione seu gênero.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="journey-gender-after-code-label" shrink sx={{ position: 'static', transform: 'none' }}>
              Gênero
            </InputLabel>
            <Select
              data-app-id="journey-gender-after-code-select"
              id="journey-gender-after-code-select"
              labelId="journey-gender-after-code-label"
              onChange={(event) => setGender(event.target.value)}
              size="small"
              value={gender}
            >
              <MenuItem value="feminino">Feminino</MenuItem>
              <MenuItem value="masculino">Masculino</MenuItem>
            </Select>
          </FormControl>
          <Button
            data-app-id="journey-gender-after-code-continue"
            disabled={!gender}
            fullWidth
            onClick={() => {
              setStoreGender(mapGenderToStore(gender));
              push('equipment');
            }}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Continuar
          </Button>
        </>
      ) : null}

      {step === 'manual_person' ? (
        <>
          {cardHeader}
          <Typography align="center" sx={{ mb: 2 }} variant="subtitle1">
            Conte um pouco sobre você.
          </Typography>
          <StandardJourneyInput appId="journey-manual-name" label="Nome completo" onChange={setName} value={name} />
          <Box
            data-app-id="journey-manual-social-group"
            sx={(theme) => ({
              ...(socialNameEnabled && {
                bgcolor:
                  theme.palette.mode === 'dark' ? theme.palette.background.default : theme.customColors.backgroundMute,
                borderRadius: 1,
                gap: 1,
                p: 1
              }),
              display: 'flex',
              flexDirection: 'column',
              mt: 1
            })}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, minWidth: 0 }}>
                <Checkbox
                  checked={socialNameEnabled}
                  data-app-id="journey-manual-social-checkbox"
                  onChange={(event) => setSocialNameEnabled(event.target.checked)}
                  size="small"
                />
                <Typography sx={{ fontWeight: 500 }} variant="caption">
                  Tenho um nome social e desejo usá-lo.
                </Typography>
              </Box>
              <StandardJourneyHintIcon
                appId="journey-manual-social-help"
                title="O nome social é escolhido por pessoas cujo nome civil não reflete sua identidade de gênero, conforme direito assegurado pelo Decreto 8727/16."
              />
            </Box>
            {socialNameEnabled ? (
              <Box sx={{ '& .MuiFormControl-root': { mt: 0 } }}>
                <StandardJourneyInput
                  appId="journey-manual-social"
                  label="Nome social"
                  onChange={setSocialName}
                  value={socialName}
                />
              </Box>
            ) : null}
          </Box>
          <StandardJourneyInput
            appId="journey-manual-cpf"
            label="CPF"
            onChange={(v) => setManualCpf(onlyDigitsCpf(v))}
            value={formatBrazilianCpf(manualCpf)}
          />
          <StandardJourneyInput
            appId="journey-manual-phone"
            label="Telefone"
            mask="phone"
            onChange={setPhone}
            value={phone}
          />
          <StandardJourneyInput
            appId="journey-manual-email"
            error={emailError}
            errorMessage={ERROR_MESSAGES.email}
            label="E-mail"
            onChange={(v) => {
              setEmail(v);
              setEmailError(false);
              setConfirmEmailError(false);
            }}
            type="email"
            value={email}
          />
          <StandardJourneyInput
            appId="journey-manual-confirm-email"
            error={confirmEmailError}
            errorMessage={ERROR_MESSAGES.confirmEmail}
            label="Confirmar E-mail"
            onChange={(v) => {
              setConfirmEmail(v);
              setConfirmEmailError(false);
            }}
            type="email"
            value={confirmEmail}
          />
          <StandardJourneyInput
            appId="journey-manual-birthdate"
            label="Data de nascimento"
            mask="birthdate"
            onChange={setBirthDate}
            placeholder="DD/MM/AAAA"
            value={birthDate}
          />
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="journey-manual-gender-label" shrink sx={{ position: 'static', transform: 'none' }}>
              Gênero
            </InputLabel>
            <Select
              id="journey-manual-gender-select"
              labelId="journey-manual-gender-label"
              onChange={(event) => setGender(event.target.value)}
              size="small"
              value={gender}
            >
              <MenuItem value="feminino">Feminino</MenuItem>
              <MenuItem value="masculino">Masculino</MenuItem>
            </Select>
          </FormControl>
          <Button
            data-app-id="journey-manual-continue"
            disabled={!canContinueManual}
            fullWidth
            onClick={handleManualContinue}
            size="large"
            sx={{ mt: 2 }}
            variant="contained"
          >
            Continuar
          </Button>
        </>
      ) : null}

      {step === 'equipment' ? (
        <>
          {cardHeader}
          <Typography align="center" sx={{ mb: 2 }} variant="subtitle1">
            Agora fale um pouco sobre o seu veículo.
          </Typography>
          <Typography sx={{ mb: 1 }} variant="subtitle2">
            Tipo de veículo
          </Typography>
          <Box sx={{ display: 'grid', gap: '16px', gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' } }}>
            {EQUIPMENT_OPTIONS.map((opt) => {
              const selected = equipmentKey === opt.id;
              const Icon = EQUIPMENT_OPTION_ICON[opt.id];
              return (
                <Box key={opt.id}>
                  <Box
                    aria-pressed={selected}
                    data-app-id={`journey-equipment-${opt.id}`}
                    onClick={() => setEquipmentKey(opt.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setEquipmentKey(opt.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    sx={(theme) => ({
                      alignItems: 'center',
                      border: '2px solid',
                      borderColor: selected ? theme.customColors.brandMain : theme.palette.text.disabled,
                      borderRadius: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      minHeight: 96,
                      justifyContent: 'center',
                      p: '8px',
                      position: 'relative'
                    })}
                  >
                    <Box onClick={(event) => event.stopPropagation()} sx={{ position: 'absolute', right: 8, top: 8 }}>
                      <StandardJourneyHintIcon appId={`journey-equipment-${opt.id}-help`} title={opt.description} />
                    </Box>
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '40px',
                        justifyContent: 'center',
                        width: '40px'
                      }}
                    >
                      <Icon aria-hidden selected={selected} />
                    </Box>
                    <Typography align="center" variant="caption">
                      {opt.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <StandardJourneyInput
            appId="journey-equipment-value"
            error={equipmentValueError}
            errorMessage={ERROR_MESSAGES.equipmentValue}
            label="Valor do veículo (R$)"
            onChange={(v) => {
              setEquipmentValueInput(maskCurrencyInput(v));
              setEquipmentValueError(false);
            }}
            placeholder="R$0,00"
            value={equipmentValueInput}
          />
          <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', mb: 1, mt: 1, p: '16px' }}>
            <Typography sx={{ mb: 1 }} variant="subtitle2">
              Tempo de uso
            </Typography>
            <RadioGroup
              onChange={(event) => setIsNewEquipment(event.target.value === 'novo')}
              value={isNewEquipment ? 'novo' : 'usado'}
            >
              <FormControlLabel control={<Radio value="novo" />} label="Novo" />
              <FormControlLabel control={<Radio value="usado" />} label="Usado" />
            </RadioGroup>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'action.hover',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
              px: '16px',
              py: '16px'
            }}
          >
            <Typography>Possui nota fiscal?</Typography>
            <Switch
              checked={hasInvoice}
              data-app-id="journey-equipment-invoice"
              onChange={(event) => setHasInvoice(event.target.checked)}
            />
          </Box>
          <FormControl error={yearError} fullWidth sx={{ mb: 0.5, mt: 1 }}>
            <InputLabel
              id="journey-equipment-year-label"
              shrink
              sx={{ color: yearError ? 'error.main' : undefined, position: 'static', transform: 'none' }}
            >
              Ano de fabricação
            </InputLabel>
            <Select
              displayEmpty
              error={yearError}
              id="journey-equipment-year-select"
              labelId="journey-equipment-year-label"
              onChange={(event) => {
                setYearOfManufacture(event.target.value === '' ? '' : Number(event.target.value));
                setYearError(false);
              }}
              size="small"
              value={yearOfManufacture === '' ? '' : String(yearOfManufacture)}
            >
              <MenuItem value="">
                <em>Selecionar</em>
              </MenuItem>
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
            {yearError ? (
              <StandardJourneyFieldError
                appId="journey-equipment-year-error"
                message={ERROR_MESSAGES.yearOfManufacture}
              />
            ) : null}
          </FormControl>
          <Typography sx={{ color: 'text.secondary', fontSize: 12, mb: 2 }}>
            <span>*Aceitamos equipamentos com no máximo </span>
            <strong>10 anos de uso.</strong>
            <br />
            <span>*Novo com no máximo </span>
            <strong>30 dias</strong>
            <span> para bicicletas e bicicletas elétricas.</span>
            <br />
            <span>*Novo com no máximo </span>
            <strong>60 dias</strong>
            <span> para autopropelidos, ciclomotores e motos elétricas.</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 1 }}>
            <Button
              data-app-id="journey-equipment-back"
              onClick={pop}
              sx={(theme) => ({ color: theme.customColors.tertiaryMain })}
            >
              Voltar
            </Button>
            <LoadingButton
              data-app-id="journey-equipment-submit"
              disabled={quoteSubmitting}
              loading={quoteSubmitting}
              onClick={() => {
                void submitQuote();
              }}
              variant="contained"
            >
              Ver plano
            </LoadingButton>
          </Box>
        </>
      ) : null}

      {step === 'quote_loading' ? (
        <JourneyLoadingScreen appId="journey-quote-loading" message="Preparando a sua cobertura" />
      ) : null}
      <JourneySupportDrawer appId="journey-support-drawer" onClose={() => setSupportOpen(false)} open={supportOpen} />
    </StandardJourneyBrandedScreen>
  );

  return screen;
}
